import dayjs from "dayjs";
import { useCallback, useEffect, useMemo } from "react";
import { useParams } from "react-router-dom";
import { useDispatch, useSelector } from "src/redux/hooks";
import { setPrevEntryData } from "src/redux/slices/generatorSlice";

import { useGetApi, useIsNewEntry, useIsOnline } from "../../hooks";
import { getYachtId } from "../../utils";
import { getAmbTotalType } from "./CustomInput.utils";

const isBeforeNoon = (timeString: string): boolean => {
  if (!timeString || !/^\d{2}:\d{2}$/.test(timeString)) {
    return false;
  }
  const hours = parseInt(timeString.split(":")[0], 10);
  return hours < 12;
};

const getTimeHour = (timeString: string | null | undefined): number | null => {
  if (!timeString || !/^\d{2}:\d{2}$/.test(timeString)) {
    return null;
  }

  const hours = parseInt(timeString.split(":")[0], 10);
  return Number.isNaN(hours) ? null : hours;
};

const isDuringVoyage = (
  timeString: string,
  voyageStart: string | null | undefined,
  voyageEnd: string | null | undefined,
  entryDate: string,
): boolean => {
  if (!timeString || !/^\d{2}:\d{2}$/.test(timeString) || !entryDate) {
    return true;
  }

  const parseDateTime = (dateStr: string, timeStr: string): Date => {
    const date = new Date(dateStr);
    const [hours, minutes] = timeStr.split(":").map(part => parseInt(part, 10));
    date.setHours(hours, minutes, 0, 0);
    return date;
  };

  const inputDateTime = parseDateTime(entryDate, timeString);

  const extractDateTime = (
    voyageDateTime: string | null | undefined,
  ): Date | null => {
    if (!voyageDateTime || voyageDateTime.trim() === "") return null;
    const parts = voyageDateTime.split(" - ");
    if (parts.length !== 2) return null;

    const dateStr = parts[0].trim();
    const timeStr = parts[1].trim();

    const date = new Date(dateStr);
    const [hours, minutes] = timeStr.split(":").map(part => parseInt(part, 10));
    date.setHours(hours, minutes, 0, 0);

    return date;
  };

  const startDateTime = extractDateTime(voyageStart);
  const endDateTime = extractDateTime(voyageEnd);

  const hasStartDateTime = !!startDateTime && !isNaN(startDateTime.getTime());
  const hasEndDateTime = !!endDateTime && !isNaN(endDateTime.getTime());

  if (!hasStartDateTime && !hasEndDateTime) {
    return true;
  }

  let isAfterStart = true;
  let isBeforeEnd = true;

  if (hasStartDateTime) {
    isAfterStart = inputDateTime >= startDateTime;
  }

  if (hasEndDateTime) {
    isBeforeEnd = inputDateTime <= endDateTime;
  }

  return isAfterStart && isBeforeEnd;
};

let isFetchingPrevEntryData = false;
let hasFetchedPrevEntryData = false;

interface Props {
  id: string;
  setValue: (id: string, value: any) => void;
  manualEdit: boolean;
  ambVoyageTotals: string | undefined;
  ambSpeedTotals: string | undefined;
  ambSteamingTotals: string | undefined;
}

interface DagonRow {
  time: string;
  sog: number | null;
  nm: number | null;
}

const useAmbTotals = ({
  id,
  setValue,
  manualEdit,
  ...ambProperties
}: Props) => {
  const type = useMemo(
    () => getAmbTotalType(ambProperties) as keyof typeof ambProperties,
    [ambProperties],
  );
  const isTotalLog = ambProperties[type] === "totalLog";
  const isTotalVoyage = ambProperties[type] === "totalVoyage";
  const isLast24 = ambProperties[type] === "last24";
  const shouldRunTotals =
    !manualEdit && (isTotalLog || isLast24 || isTotalVoyage);

  const { lbeidOrPartId, logbookId } = useParams();
  const isOnline = useIsOnline();
  const isNewEntry = useIsNewEntry();
  const prevEntryData = useSelector(
    state => state.generatorSlice.lbentry.prevEntryData,
  );
  const features = useSelector(state => state.generatorSlice.lbentry.features);
  const { entrydate } = useSelector(
    state => state.generatorSlice.entryDataToSave,
  );
  const voyages = useSelector(state => state.generatorSlice.voyages);
  const isDagon = features?.includes("DagonNoonReport");
  const dispatch = useDispatch();

  const { refetch } = useGetApi({
    request: "prev-entry-data",
    body: {
      params: {
        lbid: logbookId,
        partid: isNewEntry ? null : lbeidOrPartId,
        yachtid: getYachtId(),
      },
    },
    fetchOnInit: false,
  });

  /**
   * Calculatest total distance from dagonRows by summing up the positive differences between consecutive nm values.
   */
  const getTotalDistance = useCallback(
    (rows: DagonRow[], splitIndex: number): number => {
      let total = 0;
      rows.forEach((row, idx) => {
        if (
          row.nm !== null &&
          row.nm !== undefined &&
          (splitIndex > -1 ? idx > 0 : true)
        ) {
          total += row.nm - (rows[idx - 1]?.nm ?? 0);
        }
      });
      return total;
    },
    [],
  );

  /**
   * Processes previous entry and current entry rows for dagon calculations
   */
  const getNoonTripRows = useCallback(
    (
      prev: DagonRow[],
      arr: DagonRow[],
    ): { dagonRows: DagonRow[]; prevDagonRows: DagonRow[] } => {
      return {
        dagonRows: arr.filter(
          r => r.time && r.time !== "" && (getTimeHour(r.time) ?? 12) < 12,
        ),
        prevDagonRows: prev.filter(
          r => r.time && r.time !== "" && (getTimeHour(r.time) ?? 0) >= 12,
        ),
      };
    },
    [],
  );

  /**
   * Calculates passage steaming hours from noon to noon.
   * Logic: carry forward the moving state from the previous noon report,
   * then accumulate whole-hour gaps between consecutive moving rows.
   */
  const getSteamingTime = useCallback(
    (
      prevRows: DagonRow[],
      rows: DagonRow[],
      preNoonRow: DagonRow | null,
    ): number => {
      let steamingHours = 0;

      const prevWasMoving =
        prevRows.length > 0 ? (prevRows[0].sog ?? 0) > 0 : false;

      let previousHour: number =
        (preNoonRow?.sog ?? 0) > 0
          ? 12
          : (getTimeHour(prevRows[0]?.time) ?? 12);

      let isStillMoving = prevWasMoving;

      prevRows.forEach(row => {
        const currentHour = getTimeHour(row.time);
        if (currentHour === null) return;

        const currentRowIsMoving = (row.sog ?? 0) > 0;

        if (isStillMoving) {
          const normalizedCurrentHour =
            currentHour < previousHour ? currentHour + 24 : currentHour;
          steamingHours +=
            normalizedCurrentHour - (previousHour ?? currentHour);
        }

        previousHour = currentHour;
        isStillMoving = currentRowIsMoving;
      });

      rows.forEach((row, idx) => {
        const currentHour = getTimeHour(row.time);
        if (currentHour === null) return;

        const currentRowIsMoving = (row.sog ?? 0) > 0;

        if (idx === 0 && !prevWasMoving) {
          previousHour = currentHour;
          isStillMoving = currentRowIsMoving;
          return;
        }

        if (isStillMoving) {
          const normalizedCurrentHour =
            currentHour < previousHour ? currentHour + 24 : currentHour;
          steamingHours += normalizedCurrentHour - previousHour;
        }

        previousHour = currentHour;
        isStillMoving = currentRowIsMoving;
      });

      return steamingHours;
    },
    [],
  );

  const getAverageSpeed = useCallback((rows: DagonRow[]): number => {
    const speeds = rows.map(r => r.sog ?? 0).filter(sog => sog > 0);
    if (speeds.length === 0) return 0;
    const average = speeds.reduce((a, b) => a + b, 0) / speeds.length;

    console.log({ sogValues: speeds, averageSpeed: average });
    return average;
  }, []);

  const calculateTotals = useCallback(async () => {
    if (!shouldRunTotals) return;
    if (!isOnline) return;
    hasFetchedPrevEntryData =
      (prevEntryData && "list" in prevEntryData) ?? !!prevEntryData?.list;

    if (
      !hasFetchedPrevEntryData &&
      !isFetchingPrevEntryData &&
      (isNewEntry || !lbeidOrPartId || lbeidOrPartId === "null")
    ) {
      isFetchingPrevEntryData = true;
      try {
        const response = (await refetch()) as Record<string, any>;
        const list = response?.list ?? response?.data ?? {};
        dispatch(setPrevEntryData({ list }));
        hasFetchedPrevEntryData = true;
      } finally {
        isFetchingPrevEntryData = false;
      }
    }

    const prev = prevEntryData?.list;
    if (!prev) return;

    const { voyageTotals, speedTotals, steamingTotals, previousVoyage } = prev;
    // let currentVoyageStart: string | null = null;
    // let currentVoyageEnd: string | null = null;
    let firstVoyageStart: string | null = null;
    let lastVoyageEnd: string | null = null;

    const voyageIndices = Object.keys(voyages)
      .map(idx => parseInt(idx))
      .filter(idx => voyages[idx]?.start)
      .sort((a, b) => b - a);
    if (
      (voyageIndices.length > 0 && !previousVoyage) ||
      voyageIndices.length > 1
    ) {
      const lastVoyageIndex = voyageIndices[0];
      firstVoyageStart = voyages[voyageIndices[voyageIndices.length - 1]].start;
      lastVoyageEnd = voyages[lastVoyageIndex].end ?? null;
    } else if (previousVoyage?.startDate && previousVoyage?.startTime) {
      firstVoyageStart =
        dayjs(previousVoyage.startDate).format("DD MMM YYYY") +
        " - " +
        previousVoyage.startTime;
      lastVoyageEnd = null;
    }

    // if (voyageIndices.length > 0) {
    //   const lastVoyageIndex = voyageIndices[0];
    //   currentVoyageStart = voyages[lastVoyageIndex].start;
    //   currentVoyageEnd = voyages[lastVoyageIndex].end || null;
    // } else if (previousVoyage && previousVoyage.start && !previousVoyage.end) {
    //   currentVoyageStart = previousVoyage.start;
    //   currentVoyageEnd = null;
    // }

    const allRows = Array.from(document.querySelectorAll(".tripLog"))
      .map(el => el.closest("tr"))
      .filter((tr): tr is HTMLTableRowElement => !!tr);

    const rowsWithTime: HTMLTableRowElement[] = [];

    allRows.forEach((row, idx) => {
      const timeInput =
        row.querySelector<HTMLInputElement>(
          'input[data-unit="time"], input[data-unit="24lock"]',
        ) ??
        row.parentElement?.querySelectorAll<HTMLInputElement>(
          'input[data-unit="time"], input[data-unit="24lock"]',
        )?.[idx];

      if (
        !timeInput ||
        Boolean(timeInput?.dataset?.id?.startsWith("overlay_")) ||
        !timeInput.value
      )
        return false;

      let timeValue = timeInput.value;
      if (
        typeof timeValue === "object" &&
        timeValue !== null &&
        "time" in timeValue
      ) {
        timeValue = (timeValue as any).time;
      }

      if (isDagon) {
        rowsWithTime.push(row);
      } else if (isLast24) {
        const rowInVoyage = isDuringVoyage(
          timeValue,
          firstVoyageStart,
          lastVoyageEnd,
          entrydate,
        );
        if (isBeforeNoon(timeValue) && rowInVoyage) {
          rowsWithTime.push(row);
        }
      } else if (!isTotalLog) {
        const rowInVoyage = isDuringVoyage(
          timeValue,
          firstVoyageStart,
          lastVoyageEnd,
          entrydate,
        );
        if (rowInVoyage) {
          rowsWithTime.push(row);
        }
      } else {
        rowsWithTime.push(row);
      }
    });

    let newValue = 0;

    try {
      // CALCULATIONS FOR DAGON NOON REPORT
      if (isDagon && type && prev) {
        const { steamingTimeSinceNoon, previousNoonTripLog, raw } = prev;
        const { prevDagonRows, dagonRows } = getNoonTripRows(
          raw ?? [],
          rowsWithTime.map((row, idx): DagonRow => {
            const timeInput =
              row.parentElement?.querySelectorAll<HTMLInputElement>(
                'input[data-unit="time"], input[data-unit="24lock"]',
              )?.[idx];
            const sogInput = row.querySelector<HTMLInputElement>(
              ".SOG input:not([disabled])",
            );
            const distanceInput = row.querySelector<HTMLInputElement>(
              ".tripLog input:not([disabled])",
            );
            return {
              time: timeInput?.value ?? "",
              sog: sogInput?.value ? parseFloat(sogInput.value) : null,
              nm: distanceInput?.value ? parseFloat(distanceInput.value) : null,
            };
          }),
        );

        console.log({ prevEntryDagonRows: raw, dagonRows });
        if (type === "ambVoyageTotals") {
          const splitIndex = prevDagonRows.length - 1;

          const totalDistance = getTotalDistance(
            prevDagonRows.concat(dagonRows),
            splitIndex,
          );

          console.log({ previousNoonTripLog, totalDistance });
          newValue = (previousNoonTripLog ?? 0) + totalDistance;
        } else if (type === "ambSteamingTotals") {
          const preNoonIndex =
            raw.findIndex(r => (getTimeHour(r.time) ?? 0) >= 12) - 1;
          const preNoonRow =
            preNoonIndex >= 0 && preNoonIndex < raw.length
              ? raw?.[preNoonIndex]
              : null;

          const totalSteaming = getSteamingTime(
            prevDagonRows,
            dagonRows,
            preNoonRow,
          );

          console.log({ steamingTimeSinceNoon, totalSteaming });
          newValue = (steamingTimeSinceNoon ?? 0) + totalSteaming;
        } else if (type === "ambSpeedTotals") {
          const averageSpeed = getAverageSpeed(prevDagonRows.concat(dagonRows));

          newValue = averageSpeed;
        }

        setValue(id, !isNaN(newValue) ? parseFloat(newValue.toFixed(2)) : 0);
        return;
      }

      // CALCULATIONS FOR AMB 2 NOON REPORT
      if (type === "ambVoyageTotals") {
        const tripLogInputs = rowsWithTime.map(row =>
          row.querySelector<HTMLInputElement>(".tripLog input:not([disabled])"),
        );
        const firstNonEmptyInput = tripLogInputs.find(
          input => input?.value && input.value !== "",
        );

        const lastNonEmptyInput = tripLogInputs
          .filter(input => input?.value && input.value !== "")
          .pop();

        // Calculate the difference for current entry rows
        newValue =
          (parseFloat(lastNonEmptyInput?.value ?? "0") || 0) -
          (!isTotalLog ? parseFloat(firstNonEmptyInput?.value ?? "0") || 0 : 0);

        newValue < 0 && (newValue = 0);

        if (isTotalLog) {
          newValue += voyageTotals?.totalLog ?? 0;
        } else if (isLast24) {
          newValue += voyageTotals?.last24 ?? 0;
        } else if (isTotalVoyage) {
          newValue += voyageTotals?.totalVoyage ?? 0;
        }
      } else if (type === "ambSteamingTotals") {
        const sogInputs = rowsWithTime
          .map(row =>
            row.querySelector<HTMLInputElement>(".SOG input:not([disabled])"),
          )
          .filter(i => i?.value && parseFloat(i.value) > 0);

        const steamingHours = sogInputs.length;
        newValue = steamingHours;

        if (isLast24) {
          newValue += steamingTotals?.last24 ?? 0;
        } else if (isTotalVoyage) {
          newValue += steamingTotals?.totalVoyage ?? 0;
        }
      } else if (type === "ambSpeedTotals") {
        const sogValues = rowsWithTime
          .map(
            row =>
              row.querySelector<HTMLInputElement>(".SOG input:not([disabled])")
                ?.value ?? "",
          )
          .map(v => parseFloat(v) || 0)
          .concat(
            isLast24
              ? (speedTotals?.last24 ?? [])
              : (speedTotals?.totalVoyage ?? []),
          );

        const averageSpeed =
          sogValues.length > 0
            ? sogValues.reduce((a, b) => a + b, 0) / sogValues.length
            : 0;
        newValue = parseFloat(averageSpeed.toFixed(2));
      }
    } catch (err) {
      console.error(err);
    }

    setValue(id, !isNaN(newValue) ? newValue : 0);
  }, [
    id,
    isOnline,
    type,
    refetch,
    setValue,
    isTotalLog,
    isLast24,
    isTotalVoyage,
    prevEntryData,
    voyages,
    entrydate,
    dispatch,
    isNewEntry,
    lbeidOrPartId,
    getSteamingTime,
    shouldRunTotals,
    getNoonTripRows,
    getTotalDistance,
    isDagon,
  ]);

  useEffect(() => {
    if (!shouldRunTotals) return;

    // Helper to attach listeners
    const attachListeners = () => {
      const calcInputs = Array.from(
        document.querySelectorAll(
          '.tripLog input, .SOG input, input[data-unit="time"], input[data-unit="24lock"]',
        ),
      ).filter(
        i =>
          !(i as HTMLInputElement).disabled &&
          !(i as HTMLInputElement).name.startsWith("overlay_"),
      );
      calcInputs.forEach(i => {
        i.removeEventListener("blur", calculateTotals);
        i.addEventListener("blur", calculateTotals);
      });
      return calcInputs;
    };

    // Helper to detach listeners
    const detachListeners = (inputs: Element[]) => {
      inputs.forEach(i => {
        i.removeEventListener("blur", calculateTotals);
      });
    };

    let currentInputs = attachListeners();

    // Try to observe the closest parent of tripLog rows
    let container = document
      .querySelector(".tripLog")
      ?.closest(
        "table, tbody, .tripLog input, .SOG input, input[data-unit='time'], input[data-unit='24lock'], .tripLogContainer, .logbook-table, .logbookTable, .logbook-rows, .logbookRows",
      );
    if (!container) {
      // fallback: observe body
      container = document.body;
    }

    const observer = new MutationObserver(() => {
      detachListeners(currentInputs);
      currentInputs = attachListeners();
      void calculateTotals(); // recalc on structure change
    });

    observer.observe(container, {
      childList: true,
      subtree: true,
    });

    // Fallback: poll for input count changes (in case MutationObserver misses something)
    let prevInputCount = currentInputs.length;
    const interval = setInterval(() => {
      const newInputs = Array.from(
        document.querySelectorAll(
          '.tripLog input, .SOG input, input[data-unit="time"], input[data-unit="24lock"]',
        ),
      ).filter(
        i =>
          !(i as HTMLInputElement).disabled &&
          !(i as HTMLInputElement).name.startsWith("overlay_"),
      );
      if (newInputs.length !== prevInputCount) {
        detachListeners(currentInputs);
        currentInputs = attachListeners();
        prevInputCount = newInputs.length;
        void calculateTotals();
      }
    }, 500);

    return () => {
      observer.disconnect();
      clearInterval(interval);
      detachListeners(currentInputs);
    };
  }, [shouldRunTotals, isTotalLog, isLast24, calculateTotals]);

  useEffect(() => {
    if (!shouldRunTotals) return;

    if (hasFetchedPrevEntryData && prevEntryData?.list) void calculateTotals();
  }, [shouldRunTotals, prevEntryData, calculateTotals]);

  useEffect(() => {
    if (!shouldRunTotals) return;

    void calculateTotals();
  }, [shouldRunTotals, calculateTotals]);
};

export default useAmbTotals;
